import { useQuery } from "@tanstack/react-query";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Bell, AlertTriangle, Smartphone } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useTranslation } from "react-i18next";

interface HealthData {
  generated_at: string;
  tokens_by_app: Record<string, { total: number; healthy: number; failing: number; stale: number }>;
  silent_coaches_7d: Array<{ id: string; first_name?: string; last_name?: string; email?: string }>;
  silent_coaches_count: number;
  top_errors_24h: Array<{ error: string; count: number }>;
}

export function PushNotificationHealth() {
  const { t } = useTranslation("superadmin");
  const { data, isLoading, error } = useQuery({
    queryKey: ["notification-health"],
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke("notifications-management", {
        body: { action: "get_notification_health" },
      });
      if (error) throw error;
      return data as HealthData;
    },
    refetchInterval: 60_000,
  });

  if (isLoading) {
    return (
      <Card className="p-6">
        <p className="text-sm text-muted-foreground">{t("pushHealth.loading")}</p>
      </Card>
    );
  }

  if (error || !data) {
    return (
      <Card className="p-6">
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>{t("pushHealth.error")}</AlertTitle>
          <AlertDescription>{t("pushHealth.loadError")}</AlertDescription>
        </Alert>
      </Card>
    );
  }

  return (
    <Card className="p-6">
      <div className="flex items-center gap-2 mb-4">
        <Bell className="h-5 w-5 text-primary" />
        <h2 className="text-xl font-semibold">{t("pushHealth.title")}</h2>
      </div>

      {/* Tokens per app */}
      <div className="mb-6">
        <h3 className="text-sm font-medium text-muted-foreground mb-3 flex items-center gap-2">
          <Smartphone className="h-4 w-4" />
          {t("pushHealth.fcmTokensByApp")}
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {Object.entries(data.tokens_by_app).map(([app, stats]) => (
            <div key={app} className="border rounded-lg p-3">
              <div className="flex items-center justify-between mb-2">
                <span className="font-medium">{app}</span>
                <Badge variant="outline">{stats.total} total</Badge>
              </div>
              <div className="grid grid-cols-3 gap-2 text-xs">
                <div>
                  <div className="text-green-600 font-semibold">{stats.healthy}</div>
                  <div className="text-muted-foreground">{t("pushHealth.healthy")}</div>
                </div>
                <div>
                  <div className="text-yellow-600 font-semibold">{stats.failing}</div>
                  <div className="text-muted-foreground">{t("pushHealth.failing")}</div>
                </div>
                <div>
                  <div className="text-red-600 font-semibold">{stats.stale}</div>
                  <div className="text-muted-foreground">{t("pushHealth.stale")}</div>
                </div>
              </div>
            </div>
          ))}
          {Object.keys(data.tokens_by_app).length === 0 && (
            <p className="text-sm text-muted-foreground">{t("pushHealth.noDevices")}</p>
          )}
        </div>
      </div>

      {/* Silent coaches */}
      <div className="mb-6">
        <h3 className="text-sm font-medium text-muted-foreground mb-3 flex items-center gap-2">
          <AlertTriangle className="h-4 w-4" />
          {t("pushHealth.silentCoaches")}
          <Badge variant={data.silent_coaches_count > 0 ? "destructive" : "outline"}>
            {data.silent_coaches_count}
          </Badge>
        </h3>
        {data.silent_coaches_7d.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t("pushHealth.allCoachesOk")}</p>
        ) : (
          <div className="border rounded-lg divide-y max-h-64 overflow-auto">
            {data.silent_coaches_7d.map((coach) => (
              <div key={coach.id} className="p-3 text-sm flex justify-between">
                <span>
                  {coach.first_name} {coach.last_name}
                </span>
                <span className="text-muted-foreground text-xs">{coach.email}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Top errors */}
      <div>
        <h3 className="text-sm font-medium text-muted-foreground mb-3">{t("pushHealth.topErrors")}</h3>
        {data.top_errors_24h.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t("pushHealth.noRecentErrors")}</p>
        ) : (
          <div className="space-y-2">
            {data.top_errors_24h.map((err, idx) => (
              <div key={idx} className="flex justify-between items-center border rounded-lg p-2 text-sm">
                <code className="text-xs truncate max-w-[70%]">{err.error}</code>
                <Badge variant="destructive">{err.count}</Badge>
              </div>
            ))}
          </div>
        )}
      </div>

      <p className="text-xs text-muted-foreground mt-4">
        {t("pushHealth.updatedAt")} {new Date(data.generated_at).toLocaleString("fr-FR")}
      </p>
    </Card>
  );
}
